/**
 * 2D Kuşbakışı Okey Masası
 * Üstten bakış açısı - klasik online okey görünümü
 * 3D ile aynı özelliklere sahip
 */
import { useState } from "react";
import { cn } from "../../lib/utils";
import OkeyTile from "./OkeyTile";
import { Clock, Trash2, Layers, ArrowUpDown, Undo2, Play } from "lucide-react";
import { Button } from "../ui/button";

// Taş boyutları
const TILE_WIDTH = 44;
const TILE_HEIGHT = 60;
const SLOT_WIDTH = 48; // Slot genişliği (taş + minimal padding)
const GAP_WIDTH = SLOT_WIDTH; // Boşluk = 1 slot genişliği

export default function OkeyTable2D({
  gameState,
  currentUserId,
  onDrawCenter,
  onDrawLeft,
  onDiscard,
  onOpenHand,
  selectedTiles,
  onTileSelect,
  turnTimeRemaining,
  localTiles,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dragOverIndex,
  onSortBySeries,
  onSortByPairs,
  onResetTiles,
  onOpenSeries,
  onOpenPairs,
  smartHelpInfo,
  selectedTilesValid
}) {
  const [hasDrawn, setHasDrawn] = useState(false);
  
  if (!gameState) return null;

  const { hands, discard_piles, center_pile_count, current_player_id, scores, indicator_tile, okey_tile, turn_number } = gameState;
  
  const playerOrder = gameState.game?.player_ids || Object.keys(hands || {});
  const myIndex = playerOrder.indexOf(currentUserId);
  
  const getPlayerPosition = (index) => {
    const relativeIndex = (index - myIndex + 4) % 4;
    return ["bottom", "right", "top", "left"][relativeIndex];
  };

  const positions = {
    bottom: "absolute bottom-4 left-1/2 -translate-x-1/2",
    top: "absolute top-4 left-1/2 -translate-x-1/2",
    left: "absolute left-4 top-1/2 -translate-y-1/2",
    right: "absolute right-4 top-1/2 -translate-y-1/2"
  };

  const myHand = hands?.[currentUserId];
  const isMyTurn = current_player_id === currentUserId;
  const myTiles = localTiles?.length > 0 ? localTiles : (myHand?.tiles || []);
  
  // 101 Okey kuralı: İlk oyuncu 22 taşla başlar ve direkt taş atar (çekmez)
  // 22 taş varsa zaten çekmiş sayılır
  const tileCount = myHand?.tile_count || myTiles.length;
  const mustDiscard = tileCount === 22; // 22 taş varsa direkt atmalı

  return (
    <div className="relative w-full h-full min-h-[700px] bg-gradient-to-br from-[#1a472a] via-[#2d5a3d] to-[#1a472a] rounded-2xl overflow-hidden">
      {/* Masa dokusu */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      {/* Orta alan - z-index artırıldı */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4" style={{ zIndex: 20 }}>
        {turnTimeRemaining !== null && isMyTurn && (
          <div className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg",
            turnTimeRemaining > 30 ? "bg-green-500/20 text-green-400 border border-green-500/50" :
            turnTimeRemaining > 10 ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50" :
            "bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse"
          )}>
            <Clock className="w-5 h-5" />
            <span>{turnTimeRemaining}s</span>
          </div>
        )}

        <div className="flex items-center gap-3 bg-black/30 px-4 py-2 rounded-xl backdrop-blur-sm">
          <span className="text-white/70 text-sm">Gösterge:</span>
          {indicator_tile && <OkeyTile tile={indicator_tile} size="sm" />}
        </div>

        <button
          onClick={() => {
            onDrawCenter?.();
            setHasDrawn(true);
          }}
          disabled={!isMyTurn || hasDrawn || mustDiscard}
          className={cn(
            "relative w-20 h-28 rounded-lg transition-all",
            "bg-gradient-to-br from-[#8B4513] via-[#A0522D] to-[#6B3E26]",
            "shadow-xl hover:shadow-2xl",
            isMyTurn && !hasDrawn && !mustDiscard && "cursor-pointer hover:scale-105 ring-2 ring-[#4DFFFF]/50 animate-pulse",
            (!isMyTurn || hasDrawn || mustDiscard) && "opacity-70 cursor-not-allowed"
          )}
        >
          <div className="absolute -top-1 -left-1 w-full h-full bg-[#6B3E26] rounded-lg -z-10" />
          <div className="absolute -top-2 -left-2 w-full h-full bg-[#5C3317] rounded-lg -z-20" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
            <span className="text-2xl">☕</span>
            <span className="text-xs mt-1 opacity-70">{center_pile_count}</span>
          </div>
        </button>

        {/* Kalan taş sayısı - daha belirgin */}
        <div className="bg-black/60 px-4 py-2 rounded-lg text-white font-bold text-lg shadow-lg border border-white/20">
          Kalan: {center_pile_count} taş
        </div>
      </div>

      {/* Diğer Oyuncular */}
      {playerOrder.map((playerId, index) => {
        const position = getPlayerPosition(index);
        const hand = hands?.[playerId];
        const discardPile = discard_piles?.[playerId] || [];
        const isCurrentPlayer = current_player_id === playerId;
        const isMe = playerId === currentUserId;
        const score = scores?.[playerId] || 0;

        if (isMe) return null;

        return (
          <div key={playerId} className={positions[position]}>
            <OpponentArea
              playerId={playerId}
              hand={hand}
              discardPile={discardPile}
              isCurrentPlayer={isCurrentPlayer}
              score={score}
              position={position}
              onDrawLeft={onDrawLeft}
              setHasDrawn={setHasDrawn}
            />
          </div>
        );
      })}

      {/* Benim Elim - Altta */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-4">
        {/* Seçili Taş Durumu */}
        {selectedTiles?.length > 0 && selectedTilesValid && (
          <div className="flex justify-center mb-2">
            <div className={cn(
              "px-3 py-1 rounded text-sm",
              selectedTilesValid.valid 
                ? "bg-green-500/20 text-green-400" 
                : "bg-red-500/20 text-red-400"
            )}>
              {selectedTilesValid.valid 
                ? `✓ Geçerli ${selectedTilesValid.type === 'seri' ? 'Seri' : 'Per'} (${selectedTilesValid.points} puan)`
                : `✗ Geçersiz kombinasyon (${selectedTiles.length} taş seçili)`
              }
            </div>
          </div>
        )}

        {/* Sabit 2 Katlı Ahşap Istaka */}
        <div className="flex justify-center">
          <WoodenRack
            tiles={myTiles}
            selectedTiles={selectedTiles}
            onTileSelect={onTileSelect}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            dragOverIndex={dragOverIndex}
            onSortBySeries={onSortBySeries}
            onSortByPairs={onSortByPairs}
            onResetTiles={onResetTiles}
            smartHelpInfo={smartHelpInfo}
            isMyTurn={isMyTurn}
            score={scores?.[currentUserId] || 0}
          />
        </div>

        {/* Açılan perlerim */}
        {myHand?.opened_pers?.length > 0 && (
          <div className="flex justify-center gap-4 mt-4">
            {myHand.opened_pers.map((per, i) => (
              <div key={i} className="flex gap-1 bg-black/30 p-2 rounded-lg">
                {per.tiles.map((tile, j) => (
                  <OkeyTile key={j} tile={tile} size="sm" />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Alt Aksiyon Butonları */}
        <div className="flex justify-center gap-3 mt-4">
          {isMyTurn && (
            <>
              {selectedTiles?.length === 1 && (
                <Button
                  onClick={() => {
                    onDiscard?.(selectedTiles[0]);
                    setHasDrawn(false);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold px-6"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  TAŞ AT
                </Button>
              )}
              
              {selectedTiles?.length >= 3 && (
                <>
                  <Button
                    onClick={onOpenSeries}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    DÜZAÇ ({selectedTiles.length})
                  </Button>
                  <Button
                    onClick={onOpenPairs}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6"
                  >
                    <Layers className="w-4 h-4 mr-1" />
                    ÇİFT AÇ ({selectedTiles.length})
                  </Button>
                </>
              )}
            </>
          )}
          
          {selectedTiles?.length > 0 && (
            <div className="text-white/70 flex items-center gap-2">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {selectedTiles.length} taş seçili
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sabit 2 Katlı Ahşap Istaka
 * - Üst sıra: 15 slot
 * - Alt sıra: 15 slot
 * - Taşlar gap'lerle birlikte 2 satıra akıllıca dağıtılır
 */
function WoodenRack({ 
  tiles, 
  selectedTiles, 
  onTileSelect, 
  onDragStart, 
  onDragOver, 
  onDrop, 
  onDragEnd, 
  dragOverIndex,
  onSortBySeries,
  onSortByPairs,
  onResetTiles,
  smartHelpInfo,
  isMyTurn,
  score
}) {
  const SLOTS_PER_ROW = 15;
  const RACK_WIDTH = 780;
  
  // Taşları 2 satıra akıllıca dağıt (gap'leri hesaba katarak)
  const topRowTiles = [];
  const bottomRowTiles = [];
  
  let currentRowWidth = 0;
  const maxRowWidth = SLOTS_PER_ROW * SLOT_WIDTH;
  
  tiles.forEach((tile, idx) => {
    const tileWidth = SLOT_WIDTH + (tile._gap ? GAP_WIDTH : 0);
    
    if (currentRowWidth + tileWidth <= maxRowWidth && topRowTiles.length < SLOTS_PER_ROW) {
      // Üst satıra ekle
      topRowTiles.push({ ...tile, originalIndex: idx });
      currentRowWidth += tileWidth;
    } else {
      // Alt satıra ekle
      bottomRowTiles.push({ ...tile, originalIndex: idx, _gap: bottomRowTiles.length === 0 ? false : tile._gap });
    }
  });

  return (
    <div 
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(180deg, 
          #C9A66B 0%, #B8956A 5%, #A07850 15%, #8B6914 30%,
          #7A5C12 50%, #6B4E0A 70%, #5A4008 85%, #4A3508 100%
        )`,
        boxShadow: `
          0 -4px 20px rgba(0,0,0,0.5),
          0 8px 30px rgba(0,0,0,0.6),
          inset 0 2px 4px rgba(255,255,255,0.2),
          inset 0 -4px 8px rgba(0,0,0,0.4)
        `,
        border: "4px solid #3D2906",
        width: `${RACK_WIDTH}px`
      }}
    >
      {/* Ahşap damar deseni */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(90deg, transparent, transparent 30px, rgba(0,0,0,0.1) 30px, rgba(0,0,0,0.1) 32px),
            repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(139,105,20,0.2) 60px, rgba(139,105,20,0.2) 62px)
          `
        }}
      />

      {/* Kontrol Butonları, Akıllı Yardım ve Sıra Sizde - Tek satırda */}
      <div className="flex justify-center items-center gap-2 pt-3 pb-2 relative z-10 flex-wrap">
        <Button
          onClick={onSortBySeries}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg"
        >
          <Layers className="w-4 h-4 mr-1" />
          SERİ DİZ
        </Button>
        <Button
          onClick={onSortByPairs}
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg"
        >
          <ArrowUpDown className="w-4 h-4 mr-1" />
          ÇİFT DİZ
        </Button>
        <Button
          onClick={onResetTiles}
          size="sm"
          variant="outline"
          className="border-white/50 text-white hover:bg-white/20 shadow-lg"
        >
          <Undo2 className="w-4 h-4 mr-1" />
          GERİ AL
        </Button>
        
        {/* Akıllı Yardım */}
        {smartHelpInfo && (
          <div className={cn(
            "px-3 py-1 rounded-lg font-bold text-xs",
            smartHelpInfo.type === 'can_open_101' && "bg-green-500/40 text-green-300 border border-green-500/50",
            smartHelpInfo.type === 'can_open_5_pairs' && "bg-purple-500/40 text-purple-300 border border-purple-500/50",
            smartHelpInfo.type === 'cannot_open' && "bg-black/40 text-white/80",
            smartHelpInfo.type === 'opened' && "bg-yellow-500/40 text-yellow-300"
          )}>
            {smartHelpInfo.type === 'can_open_101' && `✨ ${smartHelpInfo.points} puan`}
            {smartHelpInfo.type === 'can_open_5_pairs' && `🎯 5 Çift!`}
            {smartHelpInfo.type === 'cannot_open' && `📊 ${smartHelpInfo.points}/101 | ${smartHelpInfo.pairCount || 0} çift`}
            {smartHelpInfo.type === 'opened' && `✓ El Açık`}
          </div>
        )}
        
        {/* Puan */}
        <div className="text-[#C6A87C] font-bold text-sm px-2">
          {score} puan
        </div>
        
        {/* Sıra Sizde - En sonda */}
        {isMyTurn && (
          <div className="text-[#4DFFFF] font-bold animate-pulse text-sm px-2 border-l border-white/30">
            ⚡ Sıra Sizde!
          </div>
        )}
      </div>

      {/* Üst Sıra */}
      <RackRow 
        tiles={topRowTiles}
        rowOffset={0}
        slotsPerRow={SLOTS_PER_ROW}
        selectedTiles={selectedTiles}
        onTileSelect={onTileSelect}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        dragOverIndex={dragOverIndex}
      />

      {/* Alt Sıra */}
      <RackRow 
        tiles={bottomRowTiles}
        rowOffset={topRowTiles.length}
        slotsPerRow={SLOTS_PER_ROW}
        selectedTiles={selectedTiles}
        onTileSelect={onTileSelect}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        dragOverIndex={dragOverIndex}
      />

      <div className="h-2" />
    </div>
  );
}

/**
 * Istaka Satırı
 * Gap varsa taşın solunda boşluk
 */
function RackRow({
  tiles,
  rowOffset,
  slotsPerRow,
  selectedTiles,
  onTileSelect,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dragOverIndex
}) {
  const TOTAL_WIDTH = slotsPerRow * SLOT_WIDTH;
  
  return (
    <div 
      className="flex items-end justify-center my-1 rounded-lg"
      style={{ 
        minHeight: "64px",
        background: "rgba(0,0,0,0.2)",
        padding: "4px 8px",
        marginLeft: "8px",
        marginRight: "8px"
      }}
    >
      {tiles.map((tile, slotIdx) => {
        if (!tile) return null;
        
        const actualIdx = tile.originalIndex !== undefined ? tile.originalIndex : rowOffset + slotIdx;
        const isDropTarget = dragOverIndex === actualIdx;
        const hasGap = tile?._gap;
        
        return (
          <div 
            key={tile.tile_id || `slot-${actualIdx}`}
            className={cn(
              "flex items-end justify-center transition-all flex-shrink-0",
              isDropTarget && "bg-[#4DFFFF]/30 rounded"
            )}
            style={{ 
              width: `${SLOT_WIDTH}px`,
              height: "60px",
              marginLeft: hasGap ? `${GAP_WIDTH}px` : "0"
            }}
            onDragOver={(e) => onDragOver?.(e, actualIdx)}
            onDrop={(e) => onDrop?.(e, actualIdx)}
          >
            <div
              draggable={!!onDragStart}
              onDragStart={() => onDragStart?.(tile, actualIdx)}
              onDragEnd={onDragEnd}
              className="cursor-grab active:cursor-grabbing"
            >
              <OkeyTile
                tile={tile}
                size="md"
                selected={selectedTiles?.includes(tile.tile_id)}
                onClick={() => onTileSelect?.(tile.tile_id)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Rakip Oyuncu Alanı
 */
function OpponentArea({
  playerId,
  hand,
  discardPile,
  isCurrentPlayer,
  score,
  position,
  onDrawLeft,
  setHasDrawn
}) {
  const isVertical = position === "left" || position === "right";
  const lastDiscard = discardPile?.[discardPile.length - 1];

  return (
    <div className={cn(
      "flex flex-col items-center gap-2",
      isVertical && "flex-row"
    )}>
      <div className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-lg",
        isCurrentPlayer ? "bg-[#4DFFFF]/20 border border-[#4DFFFF]/50" : "bg-black/30",
        isVertical && "flex-col"
      )}>
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
            "bg-white/20 text-white"
          )}>
            {hand?.username?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="text-white text-sm font-medium">
            {hand?.username || playerId?.slice(-4)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#C6A87C] font-bold">{score}</span>
        </div>
      </div>

      <div className={cn(
        "flex gap-0.5",
        isVertical && "flex-col",
        position === "top" && "flex-row-reverse"
      )}>
        {Array(hand?.tile_count || 0).fill(0).map((_, i) => (
          <OkeyTile key={i} tile={{ color: "back" }} size="sm" faceDown />
        ))}
      </div>

      {lastDiscard && (
        <button
          onClick={() => {
            onDrawLeft?.(playerId);
            setHasDrawn?.(true);
          }}
          className={cn(
            "mt-2 p-1 rounded-lg transition-all",
            isCurrentPlayer && "ring-2 ring-yellow-400 animate-pulse"
          )}
          title="Soldan çek"
        >
          <OkeyTile tile={lastDiscard} size="sm" />
        </button>
      )}

      {hand?.opened_pers?.length > 0 && (
        <div className="flex gap-2 mt-2">
          {hand.opened_pers.map((per, i) => (
            <div key={i} className="flex gap-0.5 bg-black/20 p-1 rounded">
              {per.tiles.map((tile, j) => (
                <OkeyTile key={j} tile={tile} size="xs" />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
